import { IPerson } from "redmond";
import { generateUserId } from "halifax";

import { BusinessUser, UsersForBusiness } from "./fetchUsersByBusiness";
import { PassengersForBusiness } from "./fetchPassengersByBusiness";

export interface CorpBusinessId {
  businessId: string;
}

export interface TravelerBusinessSyncTarget {
  businessId: string;
  person: IPerson;
  needsAssign: boolean;
  assignUserId: string;
}

export interface PlanTravelerBusinessSyncArgs {
  person: IPerson;
  linkedDeUserId: string;
  excludeBusinessId: string;
  businesses: CorpBusinessId[];
  usersByBusiness: UsersForBusiness[];
  passengersByBusiness: PassengersForBusiness[];
}

const isNonEmptyString = (value: string | undefined): value is string =>
  value != null && value.length > 0;

export const planTravelerBusinessSync = ({
  person,
  linkedDeUserId,
  excludeBusinessId,
  businesses,
  usersByBusiness,
  passengersByBusiness,
}: PlanTravelerBusinessSyncArgs): TravelerBusinessSyncTarget[] => {
  const usersByBusinessId = new Map(
    usersByBusiness.map((entry) => [entry.businessId, entry])
  );
  const passengersByBusinessId = new Map(
    passengersByBusiness.map((entry) => [entry.businessId, entry])
  );

  return businesses
    .filter((business) => business.businessId !== excludeBusinessId)
    .flatMap((business) => {
      const businessUser: BusinessUser | undefined = usersByBusinessId
        .get(business.businessId)
        ?.users.find((user) => user.id === linkedDeUserId);
      if (businessUser == null) return [];

      const existingPerson = passengersByBusinessId
        .get(business.businessId)
        ?.passengers.find((p) => p.id === businessUser.defaultTravelerId);

      const travelerId =
        existingPerson?.id ??
        (isNonEmptyString(businessUser.defaultTravelerId)
          ? businessUser.defaultTravelerId
          : generateUserId());

      return [
        {
          businessId: business.businessId,
          person: {
            ...person,
            id: travelerId,
            createdAt: existingPerson?.createdAt ?? person.createdAt,
          },
          needsAssign: existingPerson == null,
          assignUserId: businessUser.id,
        },
      ];
    });
};
